import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { clearSsoPending, linkSsoIdentity, listSsoBindings, readSsoPending, unlinkSsoBinding, SsoApiError } from "./api";

type Binding = Awaited<ReturnType<typeof listSsoBindings>>["bindings"][number];

export function SsoLinkPanel({ token, pendingCode }: { token: string; pendingCode?: string | null }): JSX.Element {
  const { t } = useTranslation();
  const [bindings, setBindings] = useState<Binding[]>([]);
  const [pending, setPending] = useState(() => pendingCode ?? (readSsoPending()?.kind === 'link' ? readSsoPending()?.code : '') ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const listed = await listSsoBindings(token);
    setBindings(listed.bindings);
  };

  useEffect(() => {
    void reload().catch((err) => setError(err instanceof SsoApiError ? err.message : t("profilePage.sso.loadFailed")));
  }, [token]);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await work(); } catch (err) { setError(err instanceof SsoApiError ? err.message : t("profilePage.sso.actionFailed")); }
    finally { setBusy(false); }
  };

  return (
    <section aria-labelledby="sso-link-heading" className="rounded-lg border border-border-subtle bg-canvas p-5 space-y-4">
      <h2 id="sso-link-heading" className="font-bold text-base">{t("profilePage.sso.title")}</h2>
      <p className="text-sm text-fg-secondary">{t("profilePage.sso.description")}</p>
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      {pending ? (
        <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); void run(async () => { await linkSsoIdentity(pending, token); clearSsoPending(); setPending(""); await reload(); }); }}>
          <p>{t("profilePage.sso.pending")}</p>
          <Button type="submit" disabled={busy}>{t("profilePage.sso.link")}</Button>
        </form>
      ) : <p className="text-sm">{t("profilePage.sso.noPending")}</p>}
      {bindings.length === 0 ? <p className="text-sm">{t("profilePage.sso.noBindings")}</p> : (
        <ul className="divide-y divide-border-subtle">
          {bindings.map((binding) => (
            <li key={binding.id} className="py-3 flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{binding.idp_issuer}</p>
                <p className="text-xs text-fg-secondary">NameID: {binding.name_id}{binding.idp_email ? ` · ${binding.idp_email}` : ""}</p>
              </div>
              <Button variant="ghost" disabled={busy} onClick={() => void run(async () => { await unlinkSsoBinding(binding.id, token); await reload(); })}>{t("profilePage.sso.unlink")}</Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
